import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ExternalLink, PlayFilled } from '@digital-twins/design-system/icons'

import { SocialCitationPost } from '@digital-twins/shared-types'

import { SocialPlatformIcon } from './SocialPlatformIcon'
import { useSocialItemImage } from './useSocialItemImage'
import { SOCIAL_PLATFORM_LABELS } from './socialFormat'

interface SocialPostEmbedCardProps {
  post: SocialCitationPost
  testId: string
}

const CAPTION_MAX = 160

function shortCaption(text: string | undefined): string {
  const flat = (text ?? '').replace(/\s+/g, ' ').trim()
  return flat.length > CAPTION_MAX ? `${flat.slice(0, CAPTION_MAX - 1).trimEnd()}…` : flat
}

/**
 * A compact card for a social post an answer talks about: its stored still
 * (never a provider CDN link), platform, author, a short caption and a link
 * to the post. A YouTube post plays in place, through the no-cookie player
 * that loads only after a click.
 */
export function SocialPostEmbedCard(props: SocialPostEmbedCardProps) {
  const { post, testId } = props

  const { t } = useTranslation('app')
  const [isPlaying, setIsPlaying] = useState(false)
  const storedImage = useSocialItemImage(post.imagePath ?? null)

  const author = post.authorHandle ? `@${post.authorHandle}` : t('socialListening.detail.unknownAuthor')
  const caption = shortCaption(post.text)
  const canPlay = post.platform === 'youtube' && Boolean(post.embedUrl?.startsWith('https://www.youtube-nocookie.com/'))

  const onPlay = () => {
    setIsPlaying(true)
  }

  const still = storedImage.src ? (
    <img src={storedImage.src} alt="" className="h-full w-full object-cover" data-testid={`${testId}-image`} />
  ) : (
    <span className="flex h-full w-full items-center justify-center text-muted-foreground">
      <SocialPlatformIcon platform={post.platform} className="h-5 w-5" />
    </span>
  )
  const media = isPlaying && canPlay ? (
    <iframe
      src={`${post.embedUrl}?autoplay=1`}
      title={t('threads.detail.socialPost.playerTitle', { author })}
      allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
      referrerPolicy="strict-origin-when-cross-origin"
      className="aspect-video w-full rounded-lg border-0 bg-muted"
      data-testid={`${testId}-player`}
    />
  ) : canPlay ? (
    <button
      type="button"
      onClick={onPlay}
      className="relative aspect-video w-full shrink-0 overflow-hidden rounded-lg bg-muted @[26rem]:w-40"
      aria-label={t('threads.detail.socialPost.play', { author })}
      data-testid={`${testId}-play`}
    >
      {still}
      <span className="absolute inset-0 flex items-center justify-center">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-background/90 text-foreground shadow-sm">
          <PlayFilled className="ml-0.5 h-4 w-4" />
        </span>
      </span>
    </button>
  ) : (
    <div className="aspect-video w-full shrink-0 overflow-hidden rounded-lg bg-muted @[26rem]:w-40">{still}</div>
  )

  return (
    <div
      className={
        isPlaying
          ? 'flex flex-col gap-3 rounded-xl border border-border bg-card p-3'
          : 'flex flex-col gap-3 rounded-xl border border-border bg-card p-3 @[26rem]:flex-row'
      }
      data-testid={testId}
    >
      {media}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <SocialPlatformIcon platform={post.platform} className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate font-medium text-foreground" data-testid={`${testId}-author`}>{author}</span>
          <span aria-hidden>·</span>
          <span>{SOCIAL_PLATFORM_LABELS[post.platform]}</span>
        </div>
        {caption ? (
          <p className="mt-1 line-clamp-2 text-sm leading-snug text-foreground" data-testid={`${testId}-caption`}>
            {caption}
          </p>
        ) : null}
        <a
          href={post.url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-link hover:text-link-hover hover:underline"
          data-testid={`${testId}-open`}
        >
          {t('threads.detail.socialPost.open')}
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    </div>
  )
}
